import React, { useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { PUBLIC_GET_CIAM_STATUS_BY_EMAIL } from 'Queries/Queries';
import energyIcon from 'assets/pics/energy_icon.svg';
import gasIcon from 'assets/pics/gas_icon.svg';
import { useFormik } from 'formik';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';

import { PUBLIC_NO_AUTH_CONFIG } from 'components/ApolloComponent/Apollo';
import {
  getCiamEmailErrorContent,
  getCiamEmailFieldErrorMessage,
} from 'components/CiamEmail/CiamEmailErrors';
import { CustomModal } from 'components/CustomModal';
import { DEBExpertPopupContactSection } from 'components/DebFormComponents/Popup/DEBExpertPopupContactSection';
import {
  buildExpertContactValidationFields,
  getExpertContactFormErrorMessage,
  getExpertEmails,
  setCreateExpertEmailFieldErrors,
} from 'components/DebFormComponents/Popup/expertContact.utils';
import { ErrorBanner } from 'components/ErrorBanner/ErrorBanner';
import { FormActionButtons } from 'components/FormHelper/FormActionButtons';
import { FormField } from 'components/FormHelper/FormField';
import { LabelCheckbox } from 'components/LabelCheckbox/LabelCheckbox';
import { YNEFIExpertValidation } from 'components/NEFIValidation/NEFIExpertValidation';

import { BRANCH_TYPE, EIV_ENTRY_TYPE, TENANT_WITH_BIRTHDATE } from 'global/constants';

import { areExpertContactsEnabled } from 'utils/FormUtils';
import { isFachinteressent, isInstallerRegisteringOnlyForBranch } from 'utils/InstallerUtils';
import { checkCiamEmailByQuery } from 'utils/ciamEmailValidation.utils';
import { getTenantId } from 'utils/tenantUtil';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';

import './NEFINewExpertPopup.sass';

export const NEFINewExpertPopup = ({
  branchSelectionValues,
  popupOpen,
  setPopupOpen,
  mainFormik,
}) => {
  const [ciamEmailError, setCiamEmailError] = useState(null);
  const [isCheckingCiamEmail, setIsCheckingCiamEmail] = useState(false);
  const { installer } = useCurrentInstaller();

  const expertsEmails = useMemo(
    () => getExpertEmails(mainFormik?.values?.experts),
    [mainFormik?.values?.experts]
  );

  const validationSchema = useMemo(() => {
    let schema = YNEFIExpertValidation(branchSelectionValues);
    if (areExpertContactsEnabled) {
      schema = schema.shape(
        buildExpertContactValidationFields({
          forbiddenMails: [...expertsEmails, mainFormik.values.email],
          isEditing: false,
          emailRequired: true,
        })
      );
    }
    return schema;
  }, [branchSelectionValues, expertsEmails, mainFormik.values.email]);

  const formik = useFormik({
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      lastName: '',
      firstName: '',
      isExpertEnergyActive: isInstallerRegisteringOnlyForBranch(
        BRANCH_TYPE.ENERGY,
        branchSelectionValues
      ),
      isExpertGasActive: isInstallerRegisteringOnlyForBranch(
        BRANCH_TYPE.GAS,
        branchSelectionValues
      ),
      birthdate: '',
      energyValidityDate: '',
      gasValidityDate: '',
      netzportalEmail: '',
      confirmEmail: '',
      newEmail: '',
      telefon: '',
    },
  });

  const [getCiamStatusByEmail] = useLazyQuery(
    PUBLIC_GET_CIAM_STATUS_BY_EMAIL,
    PUBLIC_NO_AUTH_CONFIG
  );

  useEffect(() => {
    if (popupOpen) {
      formik.validateForm();
    }
  }, [popupOpen]);

  useEffect(() => {
    setCiamEmailError(null);
  }, [formik.values.netzportalEmail]);

  const onCancel = () => {
    formik.resetForm();
    setPopupOpen(false);
    setCiamEmailError(null);
  };

  const onSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      return;
    }

    const email = formik.values.netzportalEmail?.trim();
    if (email) {
      setIsCheckingCiamEmail(true);
      setCiamEmailError(null);
      const ciamResult = await checkCiamEmailByQuery(getCiamStatusByEmail, email);
      setIsCheckingCiamEmail(false);

      if (!ciamResult.isValid) {
        const fieldError = getCiamEmailFieldErrorMessage(ciamResult.errorType);
        setCreateExpertEmailFieldErrors(formik, fieldError);
        setCiamEmailError(getCiamEmailErrorContent(ciamResult.errorType));
        return;
      }
    }

    mainFormik.setFieldValue('experts', [
      ...mainFormik.values.experts,
      {
        ...formik.values,
        completeName: `${formik.values.firstName} ${formik.values.lastName}`,
        id: String(uniqueId()),
      },
    ]);
    formik.resetForm();
    setPopupOpen(false);
    setCiamEmailError(null);
  };

  const modalContent = () => (
    <div className="nefi-popup popup-content">
      <div className="nefi-popup-field">
        <div className="flex-row">
          <div>Für die Sparte:</div>
          {branchSelectionValues.isEnergyActive && (
            <LabelCheckbox
              label="Strom"
              id="isExpertEnergyActive"
              name="isExpertEnergyActive"
              error={Boolean(formik.errors?.noBranchSelected)}
              icon={energyIcon}
              value={formik.values.isExpertEnergyActive}
              onChange={formik.handleChange}
            />
          )}
          {branchSelectionValues.isGasActive && (
            <LabelCheckbox
              label="Gas"
              id="isExpertGasActive"
              name="isExpertGasActive"
              error={Boolean(formik.errors?.noBranchSelected)}
              icon={gasIcon}
              value={formik.values.isExpertGasActive}
              onChange={formik.handleChange}
            />
          )}
        </div>
      </div>
      {!formik.errors.noBranchSelected && (
        <>
          <div className="nefi-popup-field">
            <FormField name="lastName" formik={formik} label="Nachname*" oldValue="" fullWidth />
          </div>
          <div className="nefi-popup-field">
            <FormField name="firstName" formik={formik} label="Vorname*" oldValue="" fullWidth />
          </div>
          {areExpertContactsEnabled && (
            <>
              <DEBExpertPopupContactSection
                formik={formik}
                emailRequired
                hintText="Mit dem Zugang für den Fachinteressenten haben Sie Zugriff auf das e-fix. Sie können damit auch die Portale der Schwesterunternehmen verwenden. Mit dem Einzelzugang sehen Sie alle angelegten Fachinteressenten Ihrer Firma, und können diese bearbeiten."
              />
              {ciamEmailError && <ErrorBanner>{ciamEmailError}</ErrorBanner>}
            </>
          )}
          {TENANT_WITH_BIRTHDATE.includes(getTenantId()) && (
            <div className="nefi-popup-field">
              <FormField
                name="birthdate"
                formik={formik}
                label="Geburtsdatum*"
                oldValue=""
                fullWidth
              />
            </div>
          )}
          {formik.values.isExpertEnergyActive &&
            branchSelectionValues.branchTypeEnergy === EIV_ENTRY_TYPE.GUEST && !isFachinteressent(installer) && (
              <div className="nefi-popup-field">
                <FormField
                  name="energyValidityDate"
                  formik={formik}
                  label="Strom-Ausweis gültig bis*"
                  oldValue=""
                  fullWidth
                />
              </div>
            )}
          {formik.values.isExpertGasActive &&
            branchSelectionValues.branchTypeGas === EIV_ENTRY_TYPE.GUEST && !isFachinteressent(installer) && (
              <div className="nefi-popup-field">
                <FormField
                  name="gasValidityDate"
                  formik={formik}
                  label="Gas-Ausweis gültig bis*"
                  oldValue=""
                  fullWidth
                />
              </div>
            )}
        </>
      )}
    </div>
  );

  const getErrorMessage = (errors, isValid) => {
    const contactError = getExpertContactFormErrorMessage(errors);
    if (contactError) {
      return contactError;
    }
    if (errors.noBranchSelected) {
      return errors.noBranchSelected;
    }
    if (!isValid) {
      return 'Füllen Sie das Formular vollständig aus';
    }
    return '';
  };

  return (
    <CustomModal
      isOpen={popupOpen}
      title="Fachinteressent hinzufügen"
      closeCancelEventModal={onCancel}
      ctaButtons={
        <FormActionButtons
          hasErrors={Object.keys(formik.errors).length > 0 || Boolean(ciamEmailError)}
          titleLeft="Abbrechen"
          titleRight="Hinzufügen"
          onClickLeft={onCancel}
          onClickRight={onSubmit}
          loading={isCheckingCiamEmail}
          errorMessage={getErrorMessage(formik.errors, formik.isValid)}
        />
      }
      content={modalContent()}
    />
  );
};

NEFINewExpertPopup.propTypes = {
  branchSelectionValues: PropTypes.object,
  popupOpen: PropTypes.bool,
  setPopupOpen: PropTypes.func,
  mainFormik: PropTypes.object,
};

NEFINewExpertPopup.defaultProps = {
  branchSelectionValues: {},
  popupOpen: false,
  setPopupOpen: () => {},
  mainFormik: {},
};
